import { useHistoryLocation } from 'app/components/router/History';
import IRoute from 'app/constants/Routes/IRoute';
import { Seq } from 'immutable';
import isEqual from 'lodash/isEqual';
import { parse } from 'query-string';
import React, { createContext, PureComponent, ReactNode, FC, useContext } from 'react';
import { useConfiguredRoutes } from 'app/components/HandleRoute';

export interface IActiveRoute {
  readonly route?: IRoute;
  /**
   * @deprecated - Depending on generic types through context is not sustainable. Solution pending.
   */
  readonly params: unknown;
  /**
   * @deprecated - Handle query parameters contextually. See `createParamContext`.
   */
  readonly query: { [key: string]: string | string[] | undefined };
}

const ActiveRouteContext = createContext<IActiveRoute>({ params: {}, query: {} });

interface IProps {
  routes: Seq.Indexed<IRoute>;
}

interface IRouteMatcherProps extends IProps {
  children: (activeRoute: IActiveRoute) => ReactNode;
}

interface IDeps {
  pathname: string;
  search?: string;
}

export class RouteMatcher extends PureComponent<IDeps & IRouteMatcherProps, IActiveRoute> {
  public static getDerivedStateFromProps(
    { routes, pathname, search = '' }: IDeps & IRouteMatcherProps,
    state: IActiveRoute,
  ) {
    const route = routes.find((r) => r.match(pathname));

    const newState = {
      route,
      params: route ? route.decode(pathname) : {},
      query:  parse(search),
    };

    return !isEqual(newState, state) ? newState : null;
  }

  public readonly state: IActiveRoute = { params: {}, query: {} };

  public render() {
    return this.props.children(this.state);
  }
}

/**
 * Internal provider that is decoupled from the route provider to make testing easier.
 */
export const ActiveRouteProviderInternal: FC<IProps> = ({ routes, children }) => {
  const { pathname, search } = useHistoryLocation();
  return (
    <RouteMatcher routes={routes} pathname={pathname} search={search}>{(activeRoute) =>
      <ActiveRouteContext.Provider value={activeRoute}>
        {children}
      </ActiveRouteContext.Provider>
    }</RouteMatcher>
  );
};

/**
 * Exposes matched route params via context, plus parsed params
 * and query turned into an object
 */
export const ActiveRouteProvider: FC = ({ children }) => {
  const routes = useConfiguredRoutes();
  return (
    <ActiveRouteProviderInternal routes={routes.keySeq()}>
      {children}
    </ActiveRouteProviderInternal>
  );
};

export const GetActiveRoute = ActiveRouteContext.Consumer;
export const useActiveRoute = () => useContext(ActiveRouteContext);
